/**
 * Shared schema.org helpers for the campaign listing page (/kampanyalar).
 *
 * A campaign is a discount a venue announces, not a product: it is modelled as
 * `Offer` and tied back to the venue that runs it through
 * `offeredBy → LocalBusiness`. Nothing here is inferred — every value comes from
 * the same campaign record that renders the card, and a field that is missing in
 * the data is dropped from the schema instead of being filled with a guess.
 *
 * The panel has no price column on `campaign`, only a free-text description, so
 * `price` / `priceCurrency` / `priceValidUntil` are deliberately absent: the
 * percentages and amounts written inside the copy are prose, and parsing them
 * would publish a number the site never quotes as an actual price.
 */

import { getVenueDetailUrl } from "@/lib/venue-schema";
import unescapeHTML from "@/lib/unescape-html";

const ACTIVE_STATUS = 1;

/**
 * Campaign copy is stored as the HTML the panel editor produced. Tags are
 * dropped first so an escaped `&lt;p&gt;` can never turn back into markup, then
 * the entities are decoded and the whitespace the tags left behind is collapsed.
 */
export function getCampaignText(value: unknown) {
  if (typeof value !== "string") return undefined;

  const text = unescapeHTML(value.replace(/<[^>]*>/g, " "))
    .replace(/\s+/g, " ")
    .trim();

  return text || undefined;
}

/**
 * `date_start` / `date_end` arrive as timestamps (`2026-08-31T00:00:00+00:00`),
 * while schema.org wants a plain `YYYY-MM-DD`. The calendar date is read off the
 * string rather than through `Date`, so a non-UTC offset cannot shift a campaign
 * to the previous day.
 */
export function getSchemaDate(value: unknown) {
  if (typeof value !== "string") return undefined;

  const match = value.match(/^\d{4}-\d{2}-\d{2}/);

  return match ? match[0] : undefined;
}

/**
 * The API already hides passive and expired campaigns, but the schema must never
 * outlive the cards: a campaign that stops rendering has to disappear from the
 * markup in the same pass. ISO dates compare correctly as strings.
 */
export function isCampaignActive(campaign: any, today?: string) {
  if (campaign?.status !== undefined && campaign?.status !== ACTIVE_STATUS) {
    return false;
  }

  const endsOn = getSchemaDate(campaign?.date_end);

  if (!endsOn) return true;

  return endsOn >= (today || new Date().toISOString().slice(0, 10));
}

/**
 * Services are an explicit relation on the campaign and are rendered as the
 * badges on the card, so they are known rather than inferred. The API trims the
 * relation to the first three entries and the card shows exactly that slice, so
 * schema and page stay in sync. Campaigns without services get no `itemOffered`
 * at all — the service is not guessed from the title.
 */
export function getCampaignServices(campaign: any) {
  const services = (campaign?.services || [])
    .map((item: any) => item?.service?.descriptions?.[0]?.title?.trim())
    .filter(Boolean)
    .map((name: string) => ({ "@type": "Service", name }));

  if (!services.length) return undefined;

  return services.length === 1 ? services[0] : services;
}

/**
 * `@id` and `url` are built with the shared venue helper, so the business named
 * here is the same node the venue detail page publishes as `LocalBusiness`.
 * Only `name` and `url` are carried over: this page is not the place to restate
 * a venue's address or hours, and a campaign whose venue has no resolvable
 * detail URL is skipped rather than pointed at a made-up slug.
 */
export function buildCampaignOffer(site: any, campaign: any) {
  const description = campaign?.descriptions?.[0];
  const name = description?.title?.trim();

  const venue = campaign?.venue;
  const venueUrl = getVenueDetailUrl(site, venue);
  const venueName = venue?.descriptions?.[0]?.title?.trim();

  if (!name || !venueUrl || !venueName) return undefined;

  const text = getCampaignText(description?.description);
  const validFrom = getSchemaDate(campaign?.date_start);
  const validThrough = getSchemaDate(campaign?.date_end);
  const itemOffered = getCampaignServices(campaign);

  return {
    "@type": "Offer",
    name,
    ...(text ? { description: text } : {}),
    ...(validFrom ? { validFrom } : {}),
    ...(validThrough ? { validThrough } : {}),
    offeredBy: {
      "@type": "LocalBusiness",
      "@id": venueUrl,
      name: venueName,
      url: venueUrl,
    },
    ...(itemOffered ? { itemOffered } : {}),
  };
}

/**
 * One `ItemList` for the campaigns the page actually renders. The list grows by
 * infinite scroll, so — like the venue listing — it describes the first batch and
 * derives its count from it instead of the total.
 *
 * Deduplication is by campaign id, never by venue: the same venue may run
 * several genuinely different campaigns and each stays its own `Offer`.
 */
export function buildCampaignOffersSchema({
  site,
  campaigns,
  name,
}: {
  site: any;
  campaigns: any[];
  name?: string;
}) {
  const seen = new Set();

  const offers = (Array.isArray(campaigns) ? campaigns : [])
    .filter((campaign) => isCampaignActive(campaign))
    .filter((campaign) => {
      const id = campaign?.id;

      if (id === undefined || id === null || seen.has(id)) return false;

      seen.add(id);

      return true;
    })
    .map((campaign) => buildCampaignOffer(site, campaign))
    .filter(Boolean);

  if (!offers.length) return undefined;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    ...(name ? { name } : {}),
    numberOfItems: offers.length,
    itemListElement: offers.map((offer, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: offer,
    })),
  };
}
